import {
  Button,
  Icon,
  Select,
  SelectOptionType,
  showDialogConfirm,
  TextInput,
} from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { Control, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { useValidations } from '../../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../../locales/translations';
import { ProgramUnitSubject } from '../../api';
import { ProgramFormValues } from './ProgramForm';

interface ProgramCurriculumProps {
  control: Control<ProgramFormValues>;
  subjects: ProgramUnitSubject[];
  selectedModality: SelectOptionType | null;
  isInActiveOffer?: boolean;
}

export function ProgramCurriculum({
  control,
  subjects,
  selectedModality,
  isInActiveOffer,
}: ProgramCurriculumProps) {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_STUDY_PLANS,
  );
  const prefix = 'programForm.curriculum';
  const { msgValidations } = useValidations();

  const { setValue, getValues } = useFormContext<ProgramFormValues>();

  const { fields: units, append, remove } = useFieldArray({
    control,
    name: 'units',
  });

  // Get all already added subject IDs
  const addedSubjectIds = useMemo(() => {
    const ids = new Set<number>();
    units?.forEach((unit) => {
      unit.subjects?.forEach((subject) => {
        ids.add(subject.id);
      });
    });
    return ids;
  }, [units]);

  // Filter out already added subjects
  const availableSubjects = useMemo(() => {
    return subjects.filter((s) => !addedSubjectIds.has(s.id));
  }, [subjects, addedSubjectIds]);

  const getSubjectOptions = useCallback(() => {
    return availableSubjects.map((subject) => ({
      label: `${subject.code} - ${subject.name}`,
      value: subject.id,
      modality: subject.modality?.name || '',
      code: subject.code,
      credits: subject.credits,
    }));
  }, [availableSubjects]);

  const addUnit = useCallback(() => {
    append({ name: `Unidad ${units.length + 1}`, subjects: [] });
  }, [append, units.length]);

  const removeUnit = useCallback(
    (index: number) => {
      if (units.length > 1) {
        remove(index);
      }
    },
    [remove, units.length],
  );

  // Función para agregar una asignatura a una unidad
  const addSubjectToUnit = useCallback(
    (unitIndex: number, subjectOption: SelectOptionType) => {
      const subject = subjects.find((s) => s.id === subjectOption.value);
      if (!subject) {
        return;
      }

      const currentUnits = getValues('units');
      const newSubject: ProgramUnitSubject = {
        id: subject.id,
        name: subject.name,
        code: subject.code,
      };

      // Para forzar el cambio de estado
      const updatedUnits = [...currentUnits];
      updatedUnits[unitIndex] = {
        ...updatedUnits[unitIndex],
        subjects: [...(updatedUnits[unitIndex].subjects || []), newSubject],
      };

      setValue('units', updatedUnits, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [getValues, subjects, setValue],
  );

  const openSubjectModal = useCallback(
    (unitIndex: number) => {
      const options = getSubjectOptions();
      let selectedOption: SelectOptionType | null = null;

      const SubjectModalContent = () => {
        const [selected, setSelected] = useState<SelectOptionType | null>(null);

        return (
          <Row>
            <Col xs={12}>
              <Select
                name="subjectSearch"
                label={t(`${prefix}.courseName`)}
                options={options}
                onChange={(option) => {
                  const newOption = option as SelectOptionType | null;
                  setSelected(newOption);
                  selectedOption = newOption;
                }}
                isClearable
                isSearchable
              />
            </Col>
            <Col xs={12}>
              <TextInput
                name="code"
                label={t(`${prefix}.code`)}
                value={selected?.code || ''}
                disabled
              />
            </Col>
            <Col xs={12}>
              <TextInput
                name="credits"
                label={t(`${prefix}.credits`)}
                value={selected?.credits || ''}
                disabled
              />
            </Col>
          </Row>
        );
      };

      showDialogConfirm({
        icon: {
          name: 'information',
          color: 'primary',
        },
        title: t(`${prefix}.searchSubject`),
        children: <SubjectModalContent />,
        btnConfirm: {
          text: t(`common.actions.add`),
          onConfirm: () => {
            if (selectedOption) {
              addSubjectToUnit(unitIndex, selectedOption);
            }
          },
        },
        btnClose: {
          text: t(`common.actions.cancel`),
          onClose: () => {},
        },
      });
    },
    [getSubjectOptions, addSubjectToUnit, t, prefix],
  );

  const removeSubjectFromUnit = useCallback(
    (unitIndex: number, subjectIndex: number) => {
      const currentUnits = getValues('units');
      const newSubjects = [...(currentUnits[unitIndex].subjects || [])];
      newSubjects.splice(subjectIndex, 1);

      const updatedUnits = [...currentUnits];
      updatedUnits[unitIndex] = {
        ...updatedUnits[unitIndex],
        subjects: newSubjects,
      };

      setValue('units', updatedUnits, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [getValues, setValue],
  );

  const getSubjectCount = useCallback(
    (unitIndex: number) => {
      return units[unitIndex]?.subjects?.length || 0;
    },
    [units],
  );

  return (
    <div className="program-curriculum">
      {!selectedModality ? (
        <div className="alert alert-warning mb-3">
          {t(`${prefix}.selectModalityFirst`)}
        </div>
      ) : (
        <div className="semesters-list pb-4">
          {units.map((unit, unitIndex) => {
            return (
              <div
                key={unit.id}
                className="semester"
                style={{ width: '160px', marginRight: '10px' }}
              >
                <div className="semester-courses">
                  <div style={{ marginBottom: '10px' }}>
                    <TextInput
                      name={`units.${unitIndex}.name`}
                      label={t(`${prefix}.unitName`, { number: unitIndex + 1 })}
                      control={control}
                      rules={{ required: msgValidations.required }}
                      disabled={isInActiveOffer}
                    />
                  </div>

                  {!isInActiveOffer && (
                    <div
                      className="dashed-border"
                      style={{ width: '100%', textAlign: 'center' }}
                    >
                      <div className="circle">
                        <Button
                          text=""
                          type="button"
                          size="sm"
                          icon="plus"
                          onClick={() => openSubjectModal(unitIndex)}
                        />
                      </div>
                    </div>
                  )}

                  {units[unitIndex]?.subjects?.map((subject, subjectIndex) => (
                    <div
                      key={subject.id}
                      className="course-box"
                      style={{
                        border: '1px solid var(--secondary)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        maxWidth: '100%',
                        width: '100%',
                        minHeight: '100px',
                      }}
                    >
                      <span className="name">{subject.name}</span>
                      <div className="course-box__info">
                        <span>Cod: {subject.code}</span>
                        <span>Creditos: {subject.credits}</span>
                        {!isInActiveOffer && (
                          <span
                            className="delete"
                            onClick={() =>
                              removeSubjectFromUnit(unitIndex, subjectIndex)
                            }
                          >
                            <Icon className="trash" name="trash" size="13px" />
                          </span>
                        )}
                      </div>
                    </div>
                  ))}

                  {(!units[unitIndex]?.subjects ||
                    units[unitIndex]?.subjects?.length === 0) && (
                    <p
                      className="text-muted small text-center"
                      style={{ whiteSpace: 'normal' }}
                    >
                      {t(`${prefix}.noSubjectsInUnit`)}
                    </p>
                  )}
                </div>

                <div
                  className="credits d-flex justify-content-between align-items-center"
                  style={{ width: '100%', padding: '8px 10px' }}
                >
                  <span>
                    {t(`${prefix}.subjects`)}{' '}
                    <strong>{getSubjectCount(unitIndex)}</strong>
                  </span>
                  {!isInActiveOffer && (
                    <>
                      {units.length > 1 && (
                        <span
                          onClick={() => removeUnit(unitIndex)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Icon name="trash" size="15px" />
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {!isInActiveOffer && (
            <div
              className="semester"
              style={{
                width: '160px',
                marginRight: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                className="dashed-border circle"
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Button
                  style={{
                    height: '40px',
                    width: '40px',
                    borderRadius: '50%',
                  }}
                  text=""
                  type="button"
                  size="md"
                  icon="plus"
                  onClick={addUnit}
                  outlined
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
